import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search,
  Filter,
  CalendarDays,
  ChevronDown,
  RotateCcw,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { endOfDay, startOfDay } from "date-fns";

interface XpandPointInvoiceFiltersProps {
  onSearch: (term: string) => void;
  onFilterChange: (filters: any) => void;
  onDateRangeChange: (range: string) => void;
  onReset: () => void;
  programs?: any[];
  transferPartners?: any[];
  resetKey?: number;
}

// Helper to get start/end date for quick-pick ranges
function getDateRangeMillis(range: string) {
  const now = new Date();
  let start: Date | undefined, end: Date | undefined;
  switch (range) {
    case "30days":
      start = new Date(now);
      start.setDate(now.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "7days":
      start = new Date(now);
      start.setDate(now.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "today":
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "yesterday":
      start = new Date(now);
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setDate(now.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
      break;
    case "lastMonth":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;
    default:
      start = undefined;
      end = undefined;
  }
  return {
    startDate: start ? start.toISOString() : undefined,
    endDate: end ? end.toISOString() : undefined,
  };
}

export { getDateRangeMillis };

function toCustomDateRangeISO(range: DateRange | undefined) {
  if (!range?.from || !range?.to) return null;
  return {
    startDate: startOfDay(range.from).toISOString(),
    endDate: endOfDay(range.to).toISOString(),
  };
}

export function XpandPointInvoiceFilters({
  onSearch,
  onFilterChange,
  onDateRangeChange,
  onReset,
  programs = [],
  transferPartners = [],
  resetKey,
}: XpandPointInvoiceFiltersProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("none");
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("date");
  const [filterSelections, setFilterSelections] = useState<any>({
    status: "",
    program: "",
    transferPartner: "",
  });
  const [pendingFilterSelections, setPendingFilterSelections] = useState<any>({
    status: "",
    program: "",
    transferPartner: "",
    dateRange: "none",
  });
  const [searchInputs, setSearchInputs] = useState<any>({
    status: "",
    program: "",
    transferPartner: "",
  });
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined
  );

  const statusOptions = ["PAID", "PENDING", "OVERDUE"];
  const dateOptions = [
    { value: "none", label: "None" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "7days", label: "Last 7 Days" },
    { value: "30days", label: "Last 30 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
  ];

  useEffect(() => {
    if (filterPopoverOpen) {
      setPendingFilterSelections({ ...filterSelections });
    }
  }, [filterPopoverOpen]);

  // Watch for parent reset and clear all filter states
  useEffect(() => {
    setSearchTerm("");
    setDateRange("none");
    setFilterSelections({
      status: "",
      program: "",
      transferPartner: "",
      dateRange: "none",
      startDate: undefined,
      endDate: undefined,
    });
    setPendingFilterSelections({
      status: "",
      program: "",
      transferPartner: "",
      dateRange: "none",
      startDate: undefined,
      endDate: undefined,
    });
    setSearchInputs({
      status: "",
      program: "",
      transferPartner: "",
    });
    setCustomDateRange(undefined);
  }, [resetKey]);

  // Highlight Filter for advanced filters only (status/program/transferPartner + date presets).
  // Standalone custom date range must not count.
  const hasAdvancedDateFilter =
    Boolean(filterSelections.dateRange) &&
    filterSelections.dateRange !== "none" &&
    filterSelections.dateRange !== "custom";

  const activeFilterCount =
    ["status", "program", "transferPartner"].reduce(
      (acc, key) => acc + (filterSelections[key] ? 1 : 0),
      0
    ) + (hasAdvancedDateFilter ? 1 : 0);

  const applyFilters = () => {
    setFilterSelections({ ...pendingFilterSelections });
    const filterPayload: any = { ...pendingFilterSelections };
    if (pendingFilterSelections.program) {
      filterPayload.programCode = pendingFilterSelections.program;
    } else {
      filterPayload.programCode = undefined;
    }
    delete filterPayload.program;
    if (pendingFilterSelections.transferPartner) {
      filterPayload.transferPartnerCode =
        pendingFilterSelections.transferPartner;
      delete filterPayload.transferPartner;
    } else {
      filterPayload.transferPartnerCode = undefined;
      delete filterPayload.transferPartner;
    }
    if (
      pendingFilterSelections.dateRange === "custom" &&
      pendingFilterSelections.startDate &&
      pendingFilterSelections.endDate
    ) {
      filterPayload.startDate = pendingFilterSelections.startDate;
      filterPayload.endDate = pendingFilterSelections.endDate;
    } else if (
      pendingFilterSelections.dateRange &&
      pendingFilterSelections.dateRange !== "none"
    ) {
      const { startDate, endDate } = getDateRangeMillis(
        pendingFilterSelections.dateRange
      );
      filterPayload.startDate = startDate;
      filterPayload.endDate = endDate;
    } else {
      filterPayload.startDate = undefined;
      filterPayload.endDate = undefined;
    }
    if (pendingFilterSelections.dateRange) {
      setDateRange(pendingFilterSelections.dateRange);
      onDateRangeChange(pendingFilterSelections.dateRange);
      if (pendingFilterSelections.dateRange !== "custom") {
        setCustomDateRange(undefined);
      }
    }
    onFilterChange(filterPayload);
    setFilterPopoverOpen(false);
  };

  const handleOptionSelect = (category: string, value: string) => {
    setPendingFilterSelections((prev: any) => ({
      ...prev,
      [category]: prev[category] === value ? "" : value,
    }));
  };

  const handleSearchInput = (category: string, value: string) => {
    setSearchInputs((prev: any) => ({ ...prev, [category]: value }));
  };

  const handleClearAll = () => {
    setSearchTerm("");
    setDateRange("none");
    setFilterSelections({
      status: "",
      program: "",
      transferPartner: "",
      dateRange: "none",
      startDate: undefined,
      endDate: undefined,
    });
    setPendingFilterSelections({
      status: "",
      program: "",
      transferPartner: "",
      dateRange: "none",
      startDate: undefined,
      endDate: undefined,
    });
    setCustomDateRange(undefined);
    setSearchInputs({ status: "", program: "", transferPartner: "" });
    onSearch("");
    onReset();
  };

  const renderOptions = () => {
    switch (activeCategory) {
      case "status":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {statusOptions.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="status-filter"
                  checked={pendingFilterSelections.status === opt}
                  onChange={() => handleOptionSelect("status", opt)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">
                  {opt.charAt(0) + opt.slice(1).toLowerCase()}
                </span>
              </label>
            ))}
            {pendingFilterSelections.status && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("status", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "program":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {programs
              .filter((p: any) =>
                p.programName
                  .toLowerCase()
                  .includes((searchInputs.program || "").toLowerCase())
              )
              .map((program: any) => (
                <label
                  key={program.programCode}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="program-filter"
                    checked={
                      pendingFilterSelections.program === program.programCode
                    }
                    onChange={() =>
                      handleOptionSelect("program", program.programCode)
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">
                    {program.programName}
                  </span>
                </label>
              ))}
            {pendingFilterSelections.program && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("program", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "transferPartner":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {transferPartners
              .filter((tp: any) =>
                tp.partnerName
                  .toLowerCase()
                  .includes((searchInputs.transferPartner || "").toLowerCase())
              )
              .map((tp: any) => (
                <label
                  key={tp.partnerCode}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="transfer-partner-filter"
                    checked={
                      pendingFilterSelections.transferPartner === tp.partnerCode
                    }
                    onChange={() =>
                      handleOptionSelect("transferPartner", tp.partnerCode)
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">{tp.partnerName}</span>
                </label>
              ))}
            {pendingFilterSelections.transferPartner && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("transferPartner", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "date": {
        const advancedDateRange =
          pendingFilterSelections.dateRange &&
          pendingFilterSelections.dateRange !== "custom"
            ? pendingFilterSelections.dateRange
            : "none";

        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            <Select
              value={advancedDateRange}
              onValueChange={(v) =>
                setPendingFilterSelections((prev) => ({
                  ...prev,
                  dateRange: v,
                  startDate: undefined,
                  endDate: undefined,
                }))
              }
            >
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Choose Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Choose Date</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="lastMonth">Last Month</SelectItem>
              </SelectContent>
            </Select>
            {advancedDateRange !== "none" && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() =>
                  setPendingFilterSelections((prev) => ({
                    ...prev,
                    dateRange: "none",
                    startDate: undefined,
                    endDate: undefined,
                  }))
                }
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
      <div className="flex-1 flex gap-2 items-end">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              id="search"
              placeholder="Search by invoice ID..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearch(e.target.value);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <div className="w-64 h-10 flex items-center">
          <DatePickerWithRange
            date={customDateRange}
            setDate={(range) => {
              setCustomDateRange(range);
              const dates = toCustomDateRangeISO(range);
              if (dates) {
                const { startDate, endDate } = dates;
                setFilterSelections((prev) => ({
                  ...prev,
                  startDate,
                  endDate,
                  dateRange: "custom",
                }));
                setPendingFilterSelections((prev) => ({
                  ...prev,
                  startDate,
                  endDate,
                  dateRange: "custom",
                }));
                setDateRange("custom");
                onDateRangeChange("custom");
                onFilterChange({
                  ...filterSelections,
                  startDate,
                  endDate,
                  dateRange: "custom",
                });
              }
            }}
            placeholder="Custom Date Range"
          />
        </div>
        <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={activeFilterCount > 0 ? "default" : "outline"}
              className="flex items-center gap-2 px-3 py-2 text-sm"
              onClick={() => setFilterPopoverOpen(true)}
              size="sm"
            >
              <Filter className="h-4 w-4" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[340px] p-0" align="start" side="left">
            <div className="flex max-h-[320px] h-[320px]">
              <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                {[
                  { label: "Date", value: "date" },
                  { label: "Transfer Partner", value: "program" },
                  { label: "Status", value: "status" },
                  { label: "Program", value: "transferPartner" },
                ].map((cat) => (
                  <button
                    key={cat.value}
                    className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                      activeCategory === cat.value
                        ? "bg-white shadow text-blue-600"
                        : "text-gray-700"
                    }`}
                    onClick={() => setActiveCategory(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
                <div className="flex-1" />
                <button
                  className="text-[10px] text-gray-500 hover:underline mt-2"
                  onClick={handleClearAll}
                  type="button"
                >
                  Clear all
                </button>
              </div>
              <div className="flex-1 p-2 flex flex-col min-h-0">
                <div className="flex items-center mb-1">
                  {activeCategory !== "status" && activeCategory !== "date" && (
                    <input
                      placeholder={`Search ${
                        activeCategory === "program"
                          ? "Transfer Partner"
                          : activeCategory === "transferPartner"
                            ? "Program"
                            : activeCategory.charAt(0).toUpperCase() +
                              activeCategory.slice(1)
                      }`}
                      value={searchInputs[activeCategory] || ""}
                      onChange={(e) =>
                        handleSearchInput(activeCategory, e.target.value)
                      }
                      className="h-7 w-full text-xs border rounded px-2"
                    />
                  )}
                </div>
                <div className="text-xs font-medium flex-1 min-h-0 overflow-y-auto gap-1 flex flex-col">
                  {renderOptions()}
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    className="text-xs px-3 py-1 h-7"
                    onClick={applyFilters}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 px-3 py-2 text-sm"
          onClick={onReset}
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>
    </div>
  );
}
